import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { MAX_DURATION_MS, Recorder, RecorderError, recorderErrorMessage } from "./recorder";
import type { RecorderErrorCode, RecorderLevel, RecorderResult } from "./recorder";
import { peaksFromPcm } from "./peaks";

export type RecorderStatus = "idle" | "requesting" | "recording" | "ready";

export interface UseRecorderOptions {
  maxMs?: number;
  /** Called once a take is finalised (auto-stop included). */
  onFinish?: (result: RecorderResult) => void;
}

export interface UseRecorderApi {
  status: RecorderStatus;
  elapsedMs: number;
  maxMs: number;
  level: RecorderLevel;
  /** The finished take — kept until `reset()` so a failed upload can retry. */
  result: RecorderResult | null;
  /** Peaks for the preview waveform. */
  peaks: Float32Array | null;
  /** Object URL for the preview player. */
  previewUrl: string | null;
  /** true when the last take hit the 22 s hard limit. */
  autoStopped: boolean;
  errorCode: RecorderErrorCode | null;
  errorMessage: string | null;
  start: () => Promise<void>;
  stop: () => Promise<RecorderResult | null>;
  /** Stop and throw the take away. */
  cancel: () => void;
  /** Drop the finished take (重录). */
  reset: () => void;
}

const IDLE_LEVEL: RecorderLevel = { rms: 0, peak: 0, clipped: false };

/** React binding for `Recorder`; one instance per mounted record page. */
export function useRecorder(options: UseRecorderOptions = {}): UseRecorderApi {
  const maxMs = options.maxMs ?? MAX_DURATION_MS;
  const [status, setStatus] = useState<RecorderStatus>("idle");
  const [elapsedMs, setElapsedMs] = useState(0);
  const [level, setLevel] = useState<RecorderLevel>(IDLE_LEVEL);
  const [result, setResult] = useState<RecorderResult | null>(null);
  const [autoStopped, setAutoStopped] = useState(false);
  const [errorCode, setErrorCode] = useState<RecorderErrorCode | null>(null);

  const recorderRef = useRef<Recorder | null>(null);
  const finishingRef = useRef<Promise<RecorderResult | null> | null>(null);
  const onFinishRef = useRef(options.onFinish);
  onFinishRef.current = options.onFinish;

  const finalise = useCallback(async (): Promise<RecorderResult | null> => {
    const rec = recorderRef.current;
    if (!rec) return null;
    recorderRef.current = null;
    const take = await rec.stop();
    setResult(take);
    setElapsedMs(take.durationMs);
    setLevel(IDLE_LEVEL);
    setStatus("ready");
    onFinishRef.current?.(take);
    return take;
  }, []);

  const stop = useCallback(async (): Promise<RecorderResult | null> => {
    if (finishingRef.current) return finishingRef.current;
    const p = finalise().finally(() => {
      finishingRef.current = null;
    });
    finishingRef.current = p;
    return p;
  }, [finalise]);

  const start = useCallback(async () => {
    if (recorderRef.current) return;
    setErrorCode(null);
    setAutoStopped(false);
    setResult(null);
    setElapsedMs(0);
    setLevel(IDLE_LEVEL);
    setStatus("requesting");
    const rec = new Recorder({
      maxMs,
      onLevel: setLevel,
      onElapsed: setElapsedMs,
      onAutoStop: () => {
        setAutoStopped(true);
        void stop();
      },
    });
    recorderRef.current = rec;
    try {
      await rec.start();
      setStatus("recording");
    } catch (err) {
      recorderRef.current = null;
      setStatus("idle");
      setErrorCode(err instanceof RecorderError ? err.code : "failed");
    }
  }, [maxMs, stop]);

  const cancel = useCallback(() => {
    recorderRef.current?.cancel();
    recorderRef.current = null;
    setStatus("idle");
    setElapsedMs(0);
    setLevel(IDLE_LEVEL);
    setResult(null);
    setAutoStopped(false);
  }, []);

  const reset = useCallback(() => {
    setResult(null);
    setElapsedMs(0);
    setAutoStopped(false);
    setErrorCode(null);
    setStatus("idle");
  }, []);

  useEffect(() => () => recorderRef.current?.cancel(), []);

  const peaks = useMemo(() => (result ? peaksFromPcm(result.pcm) : null), [result]);

  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  useEffect(() => {
    if (!result) {
      setPreviewUrl(null);
      return;
    }
    const url = URL.createObjectURL(result.blob);
    setPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [result]);

  return {
    status,
    elapsedMs,
    maxMs,
    level,
    result,
    peaks,
    previewUrl,
    autoStopped,
    errorCode,
    errorMessage: errorCode ? recorderErrorMessage(errorCode) : null,
    start,
    stop,
    cancel,
    reset,
  };
}
